/**
 * `@solomon-apps/shared/sim` — public entry point.
 *
 * Mounts simulated transaction behavior behind real widget UI so
 * widgets can render in environments with no wallet, RPC, or
 * on-chain access. Used by:
 *
 *   - the internal `/showcase/trade` design QA route;
 *   - external repos (docs site, marketing site) that embed the
 *     real widget chrome with scripted outcomes.
 *
 * The contract is small on purpose. External consumers only need:
 *
 *   - {@link SimModeProvider} — context provider
 *   - {@link useSimMode} — read the context (rare; orchestrators
 *     use it internally)
 *   - {@link SimScenario}, {@link SimWallet} — input types
 *
 * Orchestrators (sim-aware versions of useSwap, useStakeTransaction,
 * etc.) are an internal implementation detail; widgets import them
 * directly from `./orchestrators/*` and consumers never touch them.
 */
export { SimModeProvider, useSimMode } from "./SimModeContext";
export type { SimModeContextValue, SimModeProviderProps, SimRealHooks } from "./SimModeContext";
export type { SimScenario, SimWallet, SimulatedPhase } from "./types";
export {
    BUY_SCHEDULE,
    LANDING_BUY_SCHEDULE,
    SELL_SCHEDULE,
    STAKE_SCHEDULE,
    UNSTAKE_SCHEDULE,
    WITHDRAW_SCHEDULE,
    MOCK_BUY_RECEIPT_ROWS,
    MOCK_SELL_RECEIPT_ROWS,
    MOCK_STAKE_RECEIPT_ROWS,
    MOCK_UNSTAKE_RECEIPT_ROWS,
    MOCK_WITHDRAW_RECEIPT_ROWS
} from "./schedules";
export type { ScheduleSet, ScheduleStep } from "./schedules";
export { useSimulatedTransaction } from "./useSimulatedTransaction";
export type { UseSimulatedTransactionConfig, UseSimulatedTransactionResult } from "./useSimulatedTransaction";

// Orchestrators — sim/real resolvers per widget. Widgets import these
// directly via `@solomon-apps/shared/sim` instead of the real hooks.
export { useSwapOrchestration } from "./orchestrators/useSwapOrchestration";
export {
    useStakeOrchestration,
    useStartUnstakeOrchestration,
    useWithdrawOrchestration
} from "./orchestrators/useStakeOrchestration";
export { useAccessControlOrchestration } from "./orchestrators/useAccessControlOrchestration";
export { useWalletModalOrchestration } from "./orchestrators/useWalletModalOrchestration";
export { useAdvertisedApyOrchestration } from "./orchestrators/useAdvertisedApyOrchestration";
export { useAnalyticsCapture } from "./orchestrators/useAnalyticsCapture";
export type { AnalyticsCapture } from "./orchestrators/useAnalyticsCapture";
export { useSimMeasureCardHeight } from "./useSimMeasureCardHeight";

// Sim-ready composite widgets. These are the same widget files
// apps/main uses (`packages/shared/src/widgets/BuyWidget.tsx` etc.) —
// the orchestrator pattern means a sim-only consumer that omits
// `TokenSelectorComponent` / `EarnContentComponent` props gets a
// clean render with no SDK imports pulled in.
export {
    BuyWidget,
    TradeWidget,
    UsdvMigrationSim,
    // Enable YaaS — the real "Enable Rewards" form + its drop-in sim
    // driver (auto-plays the verify-wallet states under SimModeProvider,
    // then crossfades into the PositionsPanel success end-state).
    YaasPositionForm,
    YaasEnableRewardsSim,
    // Scripted single-interaction driver (Set Reward Destination, Select
    // Claim/Stream, …) — opens on the form, performs one field change, submits.
    YaasEnableRewardsInteractionSim,
    // Enable Rewards "Start Earning" preview frame — the first frame the
    // sim opens on (connected, no-YaaS-yet) before advancing into the
    // form. Also exported standalone for consumers wanting the preview.
    YaasEnableRewardsPreview,
    YaasEarnProjectionCTA,
    // Positions panel — the steady-state success end-state.
    PositionsPanel,
    // YaaS live-earnings card — the active-position hero, plus its
    // drop-in sim driver (auto-plays the pending accrual) and the
    // presentational pending ticker.
    PortfolioSummary,
    PendingEarningsTicker,
    YaasEarningsSim,
    // YaaS "+New Program" doc blocks — bare, auto-playing program-builder
    // sections for the wiki. Configuration is the first; the driver holds a
    // fixture draft in local state and auto-plays the field-populate
    // sequence, then loops. No SimModeProvider needed (no scenario branch /
    // simulated transaction). `useSimScript` is the reusable auto-play
    // primitive the other blocks (Policies, Viewing, Activation, Neobank)
    // copy. (Funding is no longer a standalone block — it's a derived
    // read-only Configuration field.)
    ProgramConfiguration,
    ProgramConfigurationSim,
    // Policies is the second block — the props-only section View + its
    // drop-in sim driver (auto-plays open → pick → configure → add for two
    // policies, then loops).
    ProgramPolicies,
    ProgramPoliciesSim,
    // Adding Program is the fourth block — a phase chain (not a populate): the
    // filled-out draft's confirm surface auto-playing activation
    // (idle → pending → success → loop). The phase is mocked as local fixture
    // state; no real activate mutation is pulled in. Visually demonstrates the
    // double-submit-safe pattern (primary disabled the whole time it's pending).
    useSimScript,
    // Viewing Program is the fifth and LAST block — the settled active read
    // view of a created program (active-title header + key stats + budget/
    // timeline meters + read-only config summary + read-only policies list +
    // Qualified Users table). No populate loop: the only live motion is the
    // Next Distribution countdown ticking every second (via the shared
    // useCountdown inside NextDistributionMeter) — that's the auto-play.
    ProgramViewSim,
    // Program Activation is the SIXTH block — the activation moment: a
    // filled-out Draft, the cursor clicks "Add Program", the block swaps to
    // the read view reading "Pending", then flips to "Active" after a wait,
    // then loops. The phase is mocked as local fixture state (no real activate
    // mutation); the read view reuses the "Viewing Program" composition seeded
    // from the same values.
    ProgramActivationSim,
    // Neobank scenario — three fixture-driven blocks: a neobank configures TWO
    // programs in PMP (Sim A "Continuous Balance Rewards", Sim B "Deposit
    // Campaign" — each a cursor-driven Configuration → Policies → Activation
    // walkthrough), then the Programs page shows both Active (the settled
    // Outcome rail). Same props-only Views + fixtures as the other blocks; no
    // wallet / RPC / orgApiFetch. neobankFixtures documents the case→control
    // mapping + gaps (revenue split, KYC, one-time bonus).
    NeobankContinuousProgramSim,
    NeobankDepositCampaignProgramSim,
    NeobankProgramsOutcomeSim,
    // Mobile master-detail counterpart to the Outcome block — a cursor tour
    // selects Continuous Balance Rewards in the single-screen list and pushes
    // to its detail. Same fixtures + Continuous read view as the Outcome block.
    NeobankProgramsMobileSim,
    // Simulated pointer overlay — shared cursor primitive every sim driver
    // wires into so the viewer sees where each scripted interaction happens.
    useSimCursor
} from "../widgets";
export type { SimCursorController, SimCursorType, SimCursorMoveOptions } from "../widgets";
export type { UsdvMigrationSimBalanceUpdate, UsdvMigrationSimBalanceCase, UsdvMigrationSimProps } from "../widgets";
export type {
    // The `framed` capability every program/neobank sim block carries.
    FramableProps,
    YaasEnableRewardsSimProps,
    YaasEnableRewardsInteractionSimProps,
    YaasEnableRewardsPreviewProps,
    PositionsPanelBreakdownRow,
    PortfolioSummaryProps,
    PendingEarningsTickerProps,
    YaasEarningsSimProps,
    ProgramConfigurationProps,
    ProgramConfigurationSimProps,
    ProgramConfigControl,
    ProgramConfigDemo,
    ProgramConfigDemoPhase,
    ProgramConfig,
    ProgramConfigStatus,
    ProgramPoliciesProps,
    ProgramPoliciesProgram,
    ProgramPoliciesSimProps,
    ProgramViewSimProps,
    ProgramActivationSimProps,
    NeobankContinuousProgramSimProps,
    NeobankDepositCampaignProgramSimProps,
    NeobankProgramsOutcomeSimProps,
    NeobankProgramsMobileSimProps,
    FundingAccount,
    FundingAsset,
    Policy,
    ConfigurablePolicy,
    SimScriptStep,
    UseSimScriptOptions
} from "../widgets";
export type {
    SwapMode,
    SwapView,
    SwapSnapshot,
    CtaFeedback,
    UseSwapReturn,
    TokenSelectorProps,
    TokenSelectorComponent,
    VerificationStep,
    VerificationMethod
} from "../widgets/types";

/**
 * Recommended fixed embed-frame height (px) per exported sim block.
 *
 * Defined in the leaf module `./blockHeights` (no imports) so the sim
 * components can read their own max height without a circular import through
 * this barrel. Re-exported here for consumers. See `sim/IMPORT.md` → "Frame
 * heights".
 */
export { SIM_BLOCK_HEIGHTS } from "./blockHeights";

/**
 * SimFrame — a thin-bordered, fixed-max-height wrapper to "frame" a sim block
 * (pairs with {@link SIM_BLOCK_HEIGHTS}). The ergonomic default is the `framed`
 * prop each program/neobank sim block carries (the block owns its height);
 * `SimFrame` is the low-level mechanism + a manual/custom-height escape hatch.
 * See `sim/IMPORT.md` → Framing.
 */
export { SimFrame } from "./SimFrame";
export type { SimFrameProps } from "./SimFrame";

// Explicit current-launch variants. Legacy exports above keep their names and behavior.
export { YaasCurrentEnrollmentSim } from "../widgets/yaas/current/YaasCurrentEnrollmentSim";
export type { YaasCurrentEnrollmentSimProps } from "../widgets/yaas/current/YaasCurrentEnrollmentSim";
export { YaasCurrentEarningsSim } from "../widgets/yaas/current/YaasCurrentEarningsSim";
export type { YaasCurrentEarningsSimProps } from "../widgets/yaas/current/YaasCurrentEarningsSim";
export { ENROLLMENT_SCENES, CURRENT_RETAIL_FLAGS } from "../widgets/yaas/current/timeline";
export type { EnrollmentScene } from "../widgets/yaas/current/timeline";
